import React, { useEffect, useState } from 'react';
import { FiPlusCircle } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import moment from 'moment';
import heic2any from 'heic2any';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { EssayTimeline } from '../essay-timeline';
import { EssayConsultSkill } from './essay-consult-skill';
import { statusBadgeText } from '../utils/BadgeEssay';
import imgTia from '@/components/custom/tia/img/tia-avatar.png';
import lampIcon from '@/assets/images/icons/lamp-icon.png';
import tiaInfo from '@/assets/images/tia/TIA-INFO.png';
import { Loader } from '@/components/custom/loader';
import { TiaBanner } from '@/components/custom/tia-banner';
import { DashboardContent } from '@/layouts/dashboard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Widget, WidgetTitle } from '@/components/custom/widget';
import { CourseAboutBody, CourseAboutDescription, CourseAboutHeader, CourseAboutSubtitle, CourseAboutTitle } from '@/components/custom/course';
import { Badge } from '@/components/ui/badge';
import { SkillCardWrapper, SkillEvaluation, SkillEvaluationEssayHeader, SkillEvaluationEssayMark, SkillEvaluationEssayText, SkillEvaluationEssayTitle } from '@/components/custom/skill';
import { AvatarWrapper } from '@/components/ui/avatar';
import { EssayPage, EssayPageCardScore, EssayPageCardScoreValue, EssayPageMain, EssayPageSidebar, EssayPageSidebarSkill, EssayPageTia, EssayPageTiaContent, EssayPageTiaHeader } from '@/components/custom/essay';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FormSwitch } from '@/components/custom/form';
import { cn } from '@/lib/utils';
import { PATHS } from '@/routes/paths';

const MarkModalContentComponent = ({ content }) => (
  <>
    <WidgetTitle>{content?.title}</WidgetTitle>
    <p className="mt-3">{content?.body}</p>
  </>
);

const COMPETENCIES = [
  { name: 'Competência 1', description: 'Domínio da Norma Culta da Língua Portuguesa' },
  { name: 'Competência 2', description: 'Compreensão e Aplicação Interdisciplinar do Tema Proposto' },
  { name: 'Competência 3', description: 'Seleção e Organização Lógica de Argumentos' },
  { name: 'Competência 4', description: 'Construção Coesa e Coerente da Argumentação' },
  { name: 'Competência 5', description: 'Proposição de Intervenção Respeitosa aos Direitos Humanos' }
];

const STATUS_BADGE_COLOR = {
  accepting_proposal: 'attention',
  close_for_submissions: 'neutral',
  processing: 'warning',
  unreviewed: 'success',
  reviewed: 'success',
  annulled: 'danger',
  annulled_by_teacher: 'danger ',
  minimum_length_not_satisfied: 'danger',
  failed_to_process: 'danger',
  reviewing: 'warning',
  not_sent: 'neutral',
  ocr_review: 'warning',
  sending_by_teacher: 'warning'
};

function checkScore(score) {
  if (score <= 333) return 'danger';
  if (score <= 666) return 'warning';
  return 'success';
}

function scoreTier(score, maxScore) {
  const oneThird = maxScore / 3;
  const twoThirds = oneThird * 2;
  if (score <= oneThird) return 'danger';
  if (score <= twoThirds) return 'warning';
  return 'success';
}

export function EssayConsult({ essayId, studentId, uri }) {
  const isPlayfulStudentTheme = usePlayfulStudent();
  const navigate = useNavigate();
  const client = useClient();

  const [show, setShow] = useState(false);
  const [studentEssay, setStudentEssay] = useState({});
  const [parsedText, setParsedText] = useState(null);
  const [loading, setLoading] = useState(false);
  const [essay, setEssay] = useState({});
  const [openMarkModal, setOpenMarkModal] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [activeSubmission, setActiveSubmission] = useState(Number(studentId));
  const [markModalContent, setMarkModalContent] = useState(null);

  const fetchData = () => {
    setLoading(true);
    const essayFetch = client.fetch(['essays', essayId]);
    const submissionsFetch = client.fetch([`/essays/${essayId}/essay_students`]);
    const studentEssayFetch = client.fetch(`/essays/${essayId}/essay_students/${studentId}`);

    Promise.allSettled([essayFetch, submissionsFetch, studentEssayFetch]).then(([essayResponse, submissionsResponse, studentEssayResponse]) => {
      if (essayResponse.value.error) {
        toast.error('Erro ao buscar dados da redação');
      } else {
        setEssay(essayResponse.value.data);
      }

      if (submissionsResponse.value.error) {
        toast.error('Erro ao buscar envios da redação');
      } else {
        setSubmissions(submissionsResponse.value.data);
      }

      if (studentEssayResponse.value.error) {
        toast.error('Erro ao buscar redação enviada');
      } else {
        setStudentEssay(studentEssayResponse.value.data);
      }
      setLoading(false);
    });
  };

  const getStudentEssay = async () => {
    const { data, error } = await client.fetch(`/essays/${essayId}/essay_students/${studentId}`);
    if (error) {
      toast.error('Erro ao buscar redação');
    } else {
      setStudentEssay(data);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const convertHeicOrHeifToJpg = async imageUrl => {
    if (!imageUrl) {
      return null;
    }
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const convertedBlob = await heic2any({ blob });

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result;

        setStudentEssay(prevEssay => ({
          ...prevEssay,
          essay_image: { url: base64data }
        }));
      };
      reader.readAsDataURL(convertedBlob);
    } catch (error) {
      console.error('Erro ao converter imagem HEIC:', error);
    }
  };

  const switchDigital = useFormik({
    initialValues: {
      digital: true,
      competence: 'c1',
      points: 180
    }
  });

  useEffect(() => {
    if ((studentEssay && studentEssay?.status === 'annulled') || studentEssay?.status === 'annulled_by_teacher') {
      switchDigital.setFieldValue('digital', false);
    }

    if (studentEssay && studentEssay.essay_image && studentEssay.essay_image.url) {
      if (studentEssay.essay_image.url.includes('.heic') || studentEssay.essay_image.url.includes('.heif')) {
        convertHeicOrHeifToJpg(studentEssay.essay_image.url).then(url => {
          setStudentEssay(prevEssay => ({
            ...prevEssay,
            essay_image: { url }
          }));
        });
      }
    }
    // eslint-disable-next-line
  }, [studentEssay]);

  useEffect(() => {
    getStudentEssay();
    // eslint-disable-next-line
  }, [studentId]);

  useEffect(() => {
    renderTextWithMarks();
    // eslint-disable-next-line
  }, [studentEssay]);

  const renderTextWithMarks = () => {
    const corrections = studentEssay?.corrections?.sort((a, b) => a.start_index - b.start_index);
    const tempElement = document.createElement('div');
    tempElement.innerHTML = studentEssay?.body;
    const text = tempElement.textContent;

    tempElement.remove();

    if (!corrections || corrections.length === 0) {
      setParsedText(<>{text}</>);
    } else {
      const selections = corrections.filter(correction => correction.start_index !== -1);
      const elements = [];
      let acumulator = '';
      let selectionIndex = 0;
      let counter = 0;

      text?.split('').forEach((c, i) => {
        if (i >= selections[selectionIndex]?.start_index + counter && i < selections[selectionIndex]?.end_index + counter) {
          acumulator += c;
        } else if (i === selections[selectionIndex]?.end_index + counter) {
          const currentIndex = selectionIndex;
          elements.push(
            <mark
              key={i}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setOpenMarkModal(true);
                setMarkModalContent({
                  title: 'Marcação',
                  body: selections[currentIndex].body
                });
              }}
            >
              {acumulator}
            </mark>
          );
          acumulator = '';
          elements.push(c);

          if (selectionIndex < selections.length - 1) {
            selectionIndex += 1;
          }
        } else {
          elements.push(c);
        }
      });

      if (acumulator) {
        elements.push(
          <mark
            key={text.length}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setOpenMarkModal(true);
              setMarkModalContent({
                title: 'Marcação',
                body: selections[selectionIndex].body
              });
            }}
          >
            {acumulator}
          </mark>
        );
      }

      setParsedText(<>{elements}</>);
    }
  };

  const validSubmissions = submissions.filter(item => item.status !== 'minimum_length_not_satisfied');
  const isAnnulled = studentEssay?.status === 'annulled' || studentEssay?.status === 'annulled_by_teacher';

  if (isPlayfulStudentTheme) {
    const breadcrumbs = {
      title: 'Redação',
      nav: [
        { route: '/redacao', name: 'Redação', isActive: false },
        { route: uri, name: essay?.topic, isActive: true }
      ]
    };

    return (
      <DashboardContent
        variant="plain"
        breadcrumbs={breadcrumbs}
      >
        {loading ? (
          <Loader />
        ) : (
          <>
            <TiaBanner
              className="mb-6 mt-6"
              image={tiaInfo}
              bubbleMaxWidth={800}
              bubbleIcon={lampIcon}
              text={studentEssay?.['suggestion_to_improve'] || ''}
            >
              <div className="flex shrink-0 items-center gap-1 rounded-full border border-neutral-lighter bg-white p-1">
                <Link
                  to={PATHS.ESSAY.ABOUT(essayId)}
                  className="rounded-full px-4 py-1.5 text-sm font-semibold text-neutral-dark"
                >
                  Sobre o tema
                </Link>

                <Link
                  to={uri}
                  className="-translate-y-px rounded-full px-4 py-1.5 text-sm font-extrabold text-white shadow-[0_3px_0_var(--color-mission-dark)]"
                  style={{ background: 'var(--color-mission)' }}
                >
                  Correção
                </Link>
              </div>
            </TiaBanner>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr_320px]">
              <div className="flex flex-col gap-4">
                {essay?.['allow-multiple-submissions'] ? (
                  <>
                    <div className="rounded-2xl border border-neutral-lightest bg-white p-4 shadow-card">
                      {validSubmissions.length < 2 && (
                        <button
                          type="button"
                          onClick={() => navigate(PATHS.ESSAY.SEND(essayId))}
                          className="mb-4 flex w-full items-center justify-center gap-2 rounded-full border-2 border-[var(--color-mission)] bg-white py-2.5 text-sm font-extrabold text-[var(--color-mission)] transition-transform duration-100 active:translate-y-px"
                        >
                          <FiPlusCircle />
                          Novo envio
                        </button>
                      )}

                      <div className={cn('mb-3 flex items-center justify-between', validSubmissions.length < 2 && 'border-t border-neutral-lightest pt-3')}>
                        <h3 className="mb-0 text-base font-extrabold text-neutral-900">Envios</h3>
                        {/* Aguardar definição da regra de quantidade de envios */}
                        <span className="text-sm font-semibold text-neutral-dark">{validSubmissions?.length}/2</span>
                      </div>

                      <div className="flex flex-col">
                        {validSubmissions.map((item, index) => {
                          const isActive = activeSubmission === item?.id;
                          const isLast = index === validSubmissions.length - 1;
                          const isCorrected = ['reviewed', 'unreviewed', 'annulled', 'annulled_by_teacher'].includes(item?.status);
                          const scoreTierClassName = { danger: 'text-danger', warning: 'text-[var(--color-mission)]', success: 'text-success' }[checkScore(item?.grade)];

                          return (
                            <React.Fragment key={item?.id}>
                              <button
                                type="button"
                                onClick={() => {
                                  setActiveSubmission(item?.id);
                                  navigate(PATHS.ESSAY.CONSULT(essayId, item.id));
                                }}
                                className={cn('flex w-full items-center gap-3 rounded-xl p-3 text-left transition-colors', isActive ? 'bg-[#FFECD8]' : 'bg-white hover:bg-neutral-bright')}
                              >
                                <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-extrabold', isActive ? 'bg-[var(--color-mission)] text-white' : 'bg-neutral-lightest text-neutral-dark')}>
                                  {item?.['submission_order']}º
                                </span>
                                <span className="flex-1">
                                  <span className="block text-sm font-extrabold text-neutral-900">{item?.['submission_order']}º envio</span>
                                  <Badge
                                    variant="bright"
                                    size="xs"
                                    className="my-1"
                                  >
                                    {statusBadgeText[item?.status]}
                                  </Badge>
                                  <span className="block text-xs text-neutral-dark">em {moment(item?.['submission_date']).format('DD/MM/YYYY')}</span>
                                </span>
                                {isCorrected && (
                                  <span className="shrink-0 rounded-sm bg-white p-2 text-right shadow-md">
                                    <span className={cn('block text-lg font-extrabold', scoreTierClassName)}>{item?.status === 'annulled' || item?.status === 'annulled_by_teacher' ? 'Anulada' : item?.grade}</span>
                                    <span className="block text-xs text-neutral-dark">Pontos</span>
                                  </span>
                                )}
                              </button>

                              {!isLast && <div className="ml-7 h-3 w-px bg-neutral-lighter" />}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="rounded-2xl border border-neutral-lightest bg-white p-4 shadow-card">
                    <EssayPageCardScore>
                      <EssayPageCardScoreValue variant={checkScore(studentEssay.grade)}>{studentEssay.grade}</EssayPageCardScoreValue>
                      <span>pontos</span>
                    </EssayPageCardScore>
                    <div className="flex items-center justify-between">
                      <Badge
                        variant={STATUS_BADGE_COLOR[essay?.status]}
                        size="xs"
                      >
                        {statusBadgeText[essay?.status]}
                      </Badge>
                      <p className="mb-0 text-xs text-neutral-dark">em {moment(studentEssay?.['delivery_date']).format('DD/MM/YYYY')}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mx-auto w-full rounded-2xl border border-neutral-lightest bg-white p-5 shadow-card">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="mb-0 text-base font-extrabold text-neutral-900">Sua redação</h3>
                  {!isAnnulled && (
                    <FormSwitch
                      label={switchDigital.values.digital ? 'Digital' : 'Cursiva'}
                      checked={switchDigital.values.digital}
                      onCheckedChange={checked => switchDigital.setFieldValue('digital', checked)}
                    />
                  )}
                </div>

                {switchDigital?.values?.digital ? (
                  <p className="mb-0 whitespace-pre-line text-sm leading-relaxed text-neutral-dark">{parsedText}</p>
                ) : (
                  <img
                    className="w-full cursor-pointer rounded-xl"
                    src={studentEssay?.essay_image?.url}
                    alt="prova digitalizada"
                    onClick={() => setShow(true)}
                  />
                )}
              </div>

              <div className="flex flex-col gap-4">
                {studentEssay?.['grammar_quality'] && (
                  <div className="rounded-2xl border border-neutral-lightest bg-white p-4 shadow-card">
                    <h3 className="mb-2 text-base font-extrabold text-neutral-900">Avaliação ortográfica</h3>
                    <span
                      className={cn(
                        'inline-block rounded-full px-3 py-1 text-sm font-extrabold text-white',
                        studentEssay['grammar_quality'] === 'Boa' ? 'bg-success' : studentEssay['grammar_quality'] === 'Mediana' ? 'bg-[var(--color-mission)]' : 'bg-danger'
                      )}
                    >
                      {studentEssay['grammar_quality']}
                    </span>
                  </div>
                )}

                {studentEssay?.competencies?.length > 0 && (
                  <div className="rounded-2xl border border-neutral-lightest bg-white p-4 shadow-card">
                    <h3 className="mb-3 text-base font-extrabold text-neutral-900">Avaliação de competências</h3>
                    <div className="flex flex-col gap-3">
                      {studentEssay.competencies.map((competence, i) => {
                        const tier = scoreTier(competence?.score, competence?.['max_grade']);
                        const item = COMPETENCIES[i];
                        return (
                          <div
                            key={i}
                            className="rounded-xl border border-neutral-lightest p-4"
                          >
                            <span className="text-xs font-semibold text-neutral-dark">{item?.name}</span>
                            <h4 className="mb-2 mt-0.5 text-sm font-extrabold text-neutral-900">{item?.description}</h4>

                            <div className={cn('mb-2 rounded-full px-3 py-1.5 text-center text-sm font-extrabold text-white', tier === 'danger' ? 'bg-danger' : tier === 'warning' ? 'bg-[var(--color-mission)]' : 'bg-success')}>
                              {competence?.score}/{competence?.['max_grade']} pts
                            </div>

                            {competence?.level && <p className="mb-1 text-xs font-bold text-neutral-900">Nível {competence.level}</p>}
                            {competence?.comment && <p className="mb-0 text-xs text-neutral-dark">{competence.comment}</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <Dialog
          open={show}
          onOpenChange={() => setShow(false)}
        >
          <DialogContent size="lg">
            <DialogTitle className="sr-only">Prova digitalizada</DialogTitle>
            <img
              src={studentEssay?.essay_image?.url}
              alt="prova digitalizada"
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={openMarkModal}
          onOpenChange={() => setOpenMarkModal(false)}
        >
          <DialogContent size="md">
            <DialogTitle className="sr-only">Marcação</DialogTitle>
            <MarkModalContentComponent content={markModalContent} />
          </DialogContent>
        </Dialog>
      </DashboardContent>
    );
  }

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: '/redacao',
        name: 'Redação',
        isActive: false
      },
      {
        route: uri,
        name: essay.topic,
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <Tabs defaultValue="correcao">
        {(statusBadgeText[studentEssay?.status] === 'Correção por Professor' || statusBadgeText[studentEssay?.status] === 'Correção por IA' || statusBadgeText[studentEssay?.status] === 'Anulada') && (
          <TabsList className="mb-7">
            <TabsTrigger value="correcao">Correção</TabsTrigger>
            <TabsTrigger value="competencias">Competências</TabsTrigger>
            <TabsTrigger value="sobre">Sobre</TabsTrigger>
          </TabsList>
        )}

        {loading ? (
          <Loader />
        ) : (
          <>
            <TabsContent value="correcao">
              <EssayPage className={isAnnulled ? 'justify-start' : ''}>
                <EssayPageSidebar>
                  {essay?.['allow-multiple-submissions'] ? (
                    <Widget>
                      {/* Aguardar definição da regra de quantidade de envios */}
                      {validSubmissions.length < 2 && (
                        <div className="border-b border-solid border-[#ECE9F8]">
                          <Button
                            variant="outline"
                            size="sm"
                            fullWidth
                            className="mb-6"
                            onClick={() => navigate(`/redacao/enviar/${essayId}`)}
                          >
                            <FiPlusCircle />
                            Novo envio
                          </Button>
                        </div>
                      )}

                      <EssayTimeline
                        submissions={submissions}
                        activeSubmission={activeSubmission}
                        setActiveSubmission={setActiveSubmission}
                        essayId={essayId}
                      />
                    </Widget>
                  ) : (
                    <Widget>
                      <EssayPageCardScore>
                        <EssayPageCardScoreValue variant={checkScore(studentEssay.grade)}>{studentEssay.grade}</EssayPageCardScoreValue>
                        <span>pontos</span>
                      </EssayPageCardScore>
                      <div className="flex items-center justify-between">
                        <Badge
                          variant={STATUS_BADGE_COLOR[essay?.status]}
                          size="xs"
                        >
                          {statusBadgeText[essay?.status]}
                        </Badge>
                        <p className="mb-0 text-xs text-neutral-light">em {moment(studentEssay?.['delivery_date']).format('DD/MM/YYYY')}</p>
                      </div>

                      {isAnnulled ? (
                        <div className="mt-6 flex items-center justify-between">
                          <h2 className="mb-0">Avaliação ortográfica</h2>

                          <Badge
                            square
                            size="sm"
                            variant={studentEssay?.['grammar_quality'] === 'Boa' ? 'success' : studentEssay?.['grammar_quality'] === 'Mediana' ? 'warning' : 'danger'}
                          >
                            {studentEssay?.['grammar_quality']}
                          </Badge>
                        </div>
                      ) : null}
                    </Widget>
                  )}
                </EssayPageSidebar>

                <EssayPageMain>
                  <EssayPageTia>
                    <AvatarWrapper
                      className="size-14"
                      src={imgTia}
                      text="T.I.A"
                    />
                    <EssayPageTiaContent>
                      <EssayPageTiaHeader>
                        <h2 className="!text-sm text-[#46b0c3]">Sugestões da T.I.A para melhorar sua redação</h2>
                        <img
                          src={lampIcon}
                          className="h-auto w-4 shrink-0"
                        />
                      </EssayPageTiaHeader>
                      <p className="mb-4 text-neutral">{studentEssay?.['suggestion_to_improve']}</p>
                    </EssayPageTiaContent>
                  </EssayPageTia>

                  <SkillEvaluation>
                    <SkillEvaluationEssayHeader>
                      <SkillEvaluationEssayTitle>Sua redação</SkillEvaluationEssayTitle>
                      {!isAnnulled ? (
                        <FormSwitch
                          label={switchDigital.values.digital ? 'Digital' : 'Cursiva'}
                          checked={switchDigital.values.digital}
                          onCheckedChange={checked => {
                            switchDigital.setFieldValue('digital', checked);
                          }}
                        />
                      ) : null}
                    </SkillEvaluationEssayHeader>

                    {switchDigital?.values?.digital ? (
                      <SkillEvaluationEssayText className="overflow-visible">
                        <SkillEvaluationEssayMark className="whitespace-pre-line"> {parsedText} </SkillEvaluationEssayMark>
                      </SkillEvaluationEssayText>
                    ) : (
                      <>
                        <img
                          className="cursor-pointer"
                          src={studentEssay?.essay_image?.url}
                          alt="prova digitalizada"
                          onClick={() => setShow(true)}
                          style={{ objectFit: 'contain' }}
                        />

                        <Dialog
                          open={show}
                          onOpenChange={() => setShow(false)}
                        >
                          <DialogContent size="lg">
                            <DialogTitle className="sr-only">Prova digitalizada</DialogTitle>
                            <img
                              src={studentEssay?.essay_image?.url}
                              alt="prova digitalizada"
                            />
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                  </SkillEvaluation>
                </EssayPageMain>

                {!isAnnulled ? (
                  <EssayPageSidebarSkill>
                    {studentEssay?.['grammar_quality'] && (
                      <Widget className="mb-6">
                        <WidgetTitle className="mb-2">Avaliação ortográfica</WidgetTitle>

                        <Badge
                          square
                          variant={studentEssay?.['grammar_quality'] === 'Boa' ? 'success' : studentEssay?.['grammar_quality'] === 'Mediana' ? 'warning' : 'danger'}
                        >
                          {studentEssay?.['grammar_quality']}
                        </Badge>
                      </Widget>
                    )}

                    <Widget>
                      <WidgetTitle className="mb-4">Avaliação de competências</WidgetTitle>

                      <SkillCardWrapper>
                        {studentEssay?.competencies?.map((competence, i) => (
                          <EssayConsultSkill
                            competence={competence}
                            item={COMPETENCIES[i]}
                            key={i}
                          />
                        ))}
                      </SkillCardWrapper>
                    </Widget>
                  </EssayPageSidebarSkill>
                ) : null}

                <Dialog
                  open={openMarkModal}
                  onOpenChange={() => setOpenMarkModal(false)}
                >
                  <DialogContent size="md">
                    <DialogTitle className="sr-only">Marcação</DialogTitle>
                    <MarkModalContentComponent content={markModalContent} />
                  </DialogContent>
                </Dialog>
              </EssayPage>
            </TabsContent>

            <TabsContent value="sobre">
              <Widget className="flex flex-col gap-8">
                <CourseAboutHeader>
                  <CourseAboutTitle>Enunciado</CourseAboutTitle>

                  <CourseAboutDescription className="whitespace-pre-wrap">{essay?.statement}</CourseAboutDescription>

                  <CourseAboutDescription>
                    Tema: {essay?.topic}
                    <br />
                    {/* Título: <strong>{essay?.title}</strong> */}
                  </CourseAboutDescription>
                </CourseAboutHeader>

                <CourseAboutBody>
                  <CourseAboutTitle>Textos motivadores</CourseAboutTitle>

                  {essay?.['essay-motivator-texts']?.map((item, index) => {
                    return (
                      <article key={index}>
                        <CourseAboutSubtitle>Texto {index + 1}</CourseAboutSubtitle>
                        <CourseAboutDescription dangerouslySetInnerHTML={{ __html: item.body }} />
                        {/* <CourseAboutDescription>Disponível em: https://brasilescola.uol.com.br/portugues/preconceito-linguistico.htm. Acesso em: 19 ago. 2022 (adaptado).</CourseAboutDescription> */}
                      </article>
                    );
                  })}
                </CourseAboutBody>
              </Widget>
            </TabsContent>

            <TabsContent value="competencias">
              <div>
                <Widget className="mb-6 w-[300px]">
                  <WidgetTitle className="mb-2">Avaliação ortográfica</WidgetTitle>
                  <Badge
                    square
                    variant="success"
                  >
                    Boa
                  </Badge>
                </Widget>

                <Widget>
                  <WidgetTitle className="mb-4">Avaliação de competências</WidgetTitle>

                  <SkillCardWrapper>
                    {studentEssay?.competencies?.map((competence, i) => (
                      <EssayConsultSkill
                        competence={competence}
                        item={COMPETENCIES[i]}
                        key={i}
                      />
                    ))}
                  </SkillCardWrapper>
                </Widget>
              </div>
            </TabsContent>
          </>
        )}
      </Tabs>
    </DashboardContent>
  );
}
